import React, { FC } from 'react';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography, makeStyles, Theme } from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import RefreshIcon from '@material-ui/icons/Refresh';
import AutoAwesomeIcon from '@material-ui/icons/BubbleChart';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    margin: theme.spacing(2),
    padding: theme.spacing(4),
    borderRadius: 24,
    background: '#eef8fb',
    border: '1px solid #d4ebf1',
    boxShadow: '0 8px 24px rgba(0,0,0,0.04)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(4)
  },
  titleWrap: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center'
  },
  iconBox: {
    width: 50,
    height: 50,
    borderRadius: 6,
    background: '#cdeaf2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#00708d'
  },
  title: {
    fontSize: 20,
    fontWeight: 800,
    color: '#00708d',
    lineHeight: 1.1
  },
  subtitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#4aa1b4',
    marginTop: 4
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center'
  },
  content: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr 1fr'
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr'
    }
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 2,
    textTransform: 'uppercase',
    color: '#6bb0bd',
    marginBottom: theme.spacing(1.5)
  },
  list: {
    margin: 0,
    paddingLeft: theme.spacing(2),
    '& li': {
      marginBottom: theme.spacing(1.5),
      paddingLeft: theme.spacing(0.5)
    }
  },
  itemText: {
    lineHeight: 1.45,
    fontWeight: 500
  }
}));

export interface AISummary {
  key_issues: string[];
  major_works_done: string[];
  recurring_issues: string[];
  equipment_watch: string[];
  notesCount?: number;
  basedOnRecentNotes?: boolean;
  generatedAt?: Date;
}

interface AiSummaryCardProps {
  summary: AISummary;
  loading?: boolean;
  lastUpdatedText?: string;
  onCopy: () => void;
  onRegenerate: () => void;
}

const AiSummaryCard: FC<AiSummaryCardProps> = ({ summary, loading = false, lastUpdatedText = 'Last updated just now', onCopy, onRegenerate }) => {
  const classes = useStyles();

  const hasSummary =
    summary.key_issues.length > 0 || summary.major_works_done.length > 0 || summary.recurring_issues.length > 0 || summary.equipment_watch.length > 0;

  const renderList = (items: string[]) => (
    <ul className={classes.list}>
      {items.length > 0 ? (
        items.map((item, index) => (
          <li key={index}>
            <Typography variant='body1' className={classes.itemText}>
              {item}
            </Typography>
          </li>
        ))
      ) : (
        <li>
          <Typography variant='body1' className={classes.itemText}>
            Nothing flagged.
          </Typography>
        </li>
      )}
    </ul>
  );

  return (
    <Box className={classes.card}>
      <Box className={classes.header}>
        <Box className={classes.titleWrap}>
          <Box className={classes.iconBox}>
            <AutoAwesomeIcon />
          </Box>

          <Box>
            <Typography className={classes.title}>AI-generated summary</Typography>
            <Typography className={classes.subtitle}>
              {summary.notesCount! > 0 ? `Based on ${summary.notesCount} notes · ${lastUpdatedText}` : lastUpdatedText}
            </Typography>
          </Box>
        </Box>

        <Box className={classes.actions}>
          <Tooltip title='Copy summary'>
            <IconButton color='primary' onClick={onCopy}>
              <FileCopyIcon />
            </IconButton>
          </Tooltip>

          <Button
            color='primary'
            variant='contained'
            disableElevation
            startIcon={loading ? <CircularProgress size={16} color='inherit' /> : <RefreshIcon />}
            onClick={onRegenerate}
            disabled={loading}
          >
            {hasSummary ? 'Regenerate' : 'Generate'}
          </Button>
        </Box>
      </Box>

      <Box className={classes.content}>
        <Box>
          <Typography className={classes.sectionTitle}>Key Issues</Typography>
          {renderList(summary.key_issues)}
        </Box>

        <Box>
          <Typography className={classes.sectionTitle}>Major Works Done</Typography>
          {renderList(summary.major_works_done)}
        </Box>

        <Box>
          <Typography className={classes.sectionTitle}>Recurring Issues</Typography>
          {renderList(summary.recurring_issues)}
        </Box>

        <Box>
          <Typography className={classes.sectionTitle}>Equipment Watch</Typography>
          {renderList(summary.equipment_watch)}
        </Box>
      </Box>
    </Box>
  );
};

export default AiSummaryCard;
